import {
  Add as AddIcon,
  Note,
  Search,
  Male as MaleIcon,
  Female as FemaleIcon,
  Pets as PetsIcon,
  Description,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { toast } from "react-toastify";
import PetFormModal, { IPetFormData } from "@/fluxvet/components/PetFormModal";
import { PostVetPatient } from "@/services/fhir/vetpatient/PostVetPatient";
import { PostRelatedPerson } from "@/services/fhir/relatedperson/PostRelatedPerson";
import { PatchPatient } from "@/services/fhir/patient/PatchPatient";
import { HTTPResponseStatus } from "@/utils/enum";
import type { IRelatedPersonData, IVetPatientData } from "@/utils/interfaces";
import { oids } from "@/configs/Settings";
import dayjs from "dayjs";

// Interface para os pets
interface IPet {
  id: string;
  name: string;
  species?: string;
  breed?: string;
  sex?: string;
  photoUrl?: string;
  identifier?: string; // Identifier do pet (pode ser diferente do id)
}

// Componente para o Dialog de seleção de pet
interface PetSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (petId: string) => void;
  pets: IPet[];
  selectedPetId?: string;
  onSelectPet: (petId: string) => void;
  loading: boolean;
}

const PetSelectionDialog: React.FC<PetSelectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  pets,
  selectedPetId,
  onSelectPet,
  loading,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack>
          <Typography variant="h6" fontWeight="600" color="neutral600.main">
            Selecione o Pet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Escolha um pet para visualizar os registros
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : pets.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Nenhum pet encontrado. Cadastre um pet primeiro.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {pets.map((pet) => (
              <Card
                key={pet.id}
                variant="outlined"
                sx={{
                  borderColor:
                    selectedPetId === pet.id ? "primary.main" : "grey.300",
                  borderWidth: selectedPetId === pet.id ? 2 : 1,
                  transition: "all 0.2s",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: 2,
                  },
                }}
                onClick={() => onSelectPet(pet.id)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* Avatar do pet */}
                    <Avatar
                      src={pet.photoUrl}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "primary.light",
                      }}
                    >
                      <PetsIcon />
                    </Avatar>

                    {/* Informações do pet */}
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="600">
                        {pet.name}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        {pet.species && (
                          <Chip
                            label={pet.species}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {pet.breed && (
                          <Chip
                            label={pet.breed}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {pet.sex && (
                          <Chip
                            icon={
                              pet.sex === "male" ? <MaleIcon /> : <FemaleIcon />
                            }
                            label={pet.sex === "male" ? "Macho" : "Fêmea"}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() => {
            if (selectedPetId) {
              onConfirm(selectedPetId);
            } else {
              toast.warn("Por favor, selecione um pet");
            }
          }}
          disabled={!selectedPetId}
          sx={{ bgcolor: "#00A884", "&:hover": { bgcolor: "#008c6e" } }}
        >
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VetQuickAccess: React.FC = () => {
  const navigate = useNavigate();
  const { user, userPatient } = useSessionContext();

  // Estados para o modal de seleção de pet
  const [openPetDialog, setOpenPetDialog] = useState(false);
  const [petsList, setPetsList] = useState<IPet[]>([]);
  const [selectedPetId, setSelectedPetId] = useState<string | undefined>();
  const [loadingPets, setLoadingPets] = useState(false);

  // Estados para o modal de adicionar pet
  const [openAddPetDialog, setOpenAddPetDialog] = useState(false);
  const [petForm, setPetForm] = useState<IPetFormData>({
    name: "",
    birthDate: null,
    color: null,
    state: null,
    city: "",
    species: null,
    breed: null,
    sex: "",
    genderStatus: null,
  });
  const [petPhotoBase64, setPetPhotoBase64] = useState<string | null>(null);
  const [petPhotoPreviewUrl, setPetPhotoPreviewUrl] = useState<string | null>(
    null
  );
  const [loadingAddPet, setLoadingAddPet] = useState(false);

  // Função para buscar os pets do tutor
  const fetchPets = async () => {
    if (!user?.username || !userPatient) {
      return;
    }

    setLoadingPets(true);
    try {
      const petsResponse = await GetRelatedPerson({
        includePatient: true,
        identifier: user.username.replace(/[.-]/g, ""),
        vetRelated: true,
        active: true,
      });

      if (petsResponse?.data?.entry) {
        // Filtra apenas os recursos do tipo Patient (pets)
        const patientsOnly = petsResponse.data.entry.filter(
          (entry: any) => entry.resource.resourceType === "Patient"
        );

        // Mapeia os pets para o formato IPet
        const pets: IPet[] = patientsOnly.map((entry: any) => {
          const patient = entry.resource;

          // Busca a extensão patient-animal que contém as informações do pet
          const animalExtension = patient.extension?.find((ext: any) =>
            ext.url?.includes("patient-animal")
          );

          return {
            id: patient.id || "",
            name: patient.name?.[0]?.text || "",
            species: animalExtension?.extension?.find(
              (ext: any) => ext.url === "species"
            )?.valueCodeableConcept?.coding?.[0]?.display,
            breed: animalExtension?.extension?.find(
              (ext: any) => ext.url === "breed"
            )?.valueCodeableConcept?.coding?.[0]?.display,
            sex: patient.gender,
            photoUrl: patient.photo?.[0]?.data
              ? `data:${patient.photo[0].contentType};base64,${patient.photo[0].data}`
              : undefined,
            identifier: patient.identifier?.[0]?.value || patient.id || "",
          };
        });

        setPetsList(pets);
      } else {
        setPetsList([]);
      }
    } catch (error) {
      console.error("Erro ao buscar pets:", error);
      toast.error("Erro ao carregar lista de pets");
      setPetsList([]);
    } finally {
      setLoadingPets(false);
    }
  };

  // Abre o dialog e busca os pets
  const handleOpenRegistros = () => {
    setOpenPetDialog(true);
    setSelectedPetId(undefined);
    fetchPets();
  };

  // Fecha o dialog
  const handleClosePetDialog = () => {
    setOpenPetDialog(false);
    setSelectedPetId(undefined);
  };

  // Seleciona um pet
  const handleSelectPet = (petId: string) => {
    setSelectedPetId(petId);
  };

  // Confirma a seleção e navega para o sumário
  const handleConfirmPetSelection = () => {
    if (!selectedPetId) {
      toast.warn("Por favor, selecione um pet");
      return;
    }

    // Encontra o pet selecionado para obter o identifier
    const selectedPet = petsList.find((pet) => pet.id === selectedPetId);
    // Usa o identifier se disponível, caso contrário usa o id (mesmo padrão do MyPets.tsx)
    const patientId =
      selectedPet?.identifier || selectedPet?.id || selectedPetId;

    // Navega para o sumário com o patientId
    navigate("/summary", {
      state: {
        patientId: patientId,
      },
    });

    // Fecha o dialog
    handleClosePetDialog();
  };

  // Função para abrir o modal de adicionar pet
  const handleOpenAddPet = () => {
    setPetForm({
      name: "",
      birthDate: null,
      color: null,
      state: null,
      city: "",
      species: null,
      breed: null,
      sex: "",
      genderStatus: null,
    });
    setPetPhotoBase64(null);
    setPetPhotoPreviewUrl(null);
    setOpenAddPetDialog(true);
  };

  // Função para atualizar o formulário
  const handleFormChange = (field: keyof IPetFormData, value: any) => {
    setPetForm({
      ...petForm,
      [field]: value,
    });
  };

  // Função para validar se todos os campos obrigatórios estão preenchidos
  const isFormValid = () => {
    return (
      petForm.name?.trim() !== "" &&
      petForm.species !== null &&
      petForm.sex !== "" &&
      petForm.genderStatus !== null &&
      petForm.state !== null
    );
  };

  // Função para salvar um novo pet
  const handleSavePet = async () => {
    setLoadingAddPet(true);
    try {
      // Preparar os dados do pet no formato esperado pela API
      const vetPatientData: IVetPatientData = {
        name: petForm.name,
        gender: petForm.sex,
        ...(petForm.birthDate &&
          dayjs(petForm.birthDate).isValid() && {
            birthDate: petForm.birthDate,
          }),
        speciesCode: petForm.species?.code,
        speciesDisplay: petForm.species?.display,
        breedCode: petForm.breed?.code,
        breedDisplay: petForm.breed?.display,
        colorCode: petForm.color?.code,
        colorDisplay: petForm.color?.display,
        genderStatusCode: petForm.genderStatus?.code,
        genderStatusDisplay: petForm.genderStatus?.display,
        city: petForm.city,
        state: petForm.state?.value,
      };

      // Adiciona foto se houver
      if (petPhotoBase64) {
        vetPatientData.photo = petPhotoBase64;
      }

      // Criar novo pet
      const response = await PostVetPatient(vetPatientData);

      if (response && response.status === HTTPResponseStatus.Created) {
        toast.success("Pet cadastrado com sucesso!");

        // Criar RelatedPerson vinculando o pet ao tutor
        const tutorCPF = user?.username || "";

        const responsibleData: IRelatedPersonData = {
          patient: {
            reference: `Patient/${response.data?.id}`,
            display: response.data?.name?.[0]?.text || "",
          },
          phoneNumber:
            userPatient?.telecom?.find((t: any) => t.system === "phone")
              ?.value || "",
          relationship: {
            system: "http://fluxmed.com.br/fhir/CodeSystem/parent-animal",
            code: "owner",
          },
          ownerName: userPatient?.name?.[0]?.text || "",
          gender: userPatient?.gender || "",
          cpfResponsible: tutorCPF,
        };

        const relatedPersonResponse = await PostRelatedPerson(
          responsibleData,
          {
            reference: `Patient/${response.data?.id}`,
            display: response.data?.name?.[0]?.text || "",
          },
          true,
          true
        );

        if (
          relatedPersonResponse &&
          relatedPersonResponse.status === HTTPResponseStatus.Success
        ) {
          // Adicionar link no Patient do tutor
          const existingLinks = userPatient?.link;
          const hasLink =
            Array.isArray(existingLinks) && existingLinks.length > 0;

          const cpfIdentifier = user?.username;
          const patientId = cpfIdentifier
            ? `${oids.cpf}-${cpfIdentifier}`
            : userPatient?.id;

          if (patientId) {
            await PatchPatient({
              patientId: patientId,
              operation: "add",
              path: hasLink ? "/link/-" : "/link",
              content: hasLink
                ? {
                    other: {
                      reference: `RelatedPerson/${response.data?.id}`,
                    },
                  }
                : [
                    {
                      other: {
                        reference: `RelatedPerson/${response.data?.id}`,
                      },
                    },
                  ],
              authType: "user",
            });
          }
        }
      }

      // Limpar estados e fechar modal
      setPetPhotoBase64(null);
      setPetPhotoPreviewUrl(null);
      setOpenAddPetDialog(false);
    } catch (error) {
      console.error("Erro ao salvar pet:", error);
      toast.error("Erro ao salvar pet. Tente novamente.");
    } finally {
      setLoadingAddPet(false);
    }
  };

  // Cards de acesso rápido
  // 3 botões: Agendar Consulta, Registros e Adicionar Novo Pet
  const quickAccessCards = [
    {
      title: "Agendar Consulta",
      icon: Search,
      path: "/schedulingsearch",
      description: "Encontre veterinários e marque uma consulta",
      highlight: true,
    },
    {
      title: "Registros",
      icon: Description,
      description: "Histórico de consultas, medicamentos, exames e documentos",
      onClick: handleOpenRegistros, // Abre o dialog de seleção de pet
    },
    {
      title: "Adicionar Novo Pet",
      icon: AddIcon,
      description: "Cadastre um novo pet no sistema",
      onClick: handleOpenAddPet, // Abre o modal de adicionar pet
    },
  ];

  // Função para lidar com o clique no card
  const handleCardClick = (card: any) => {
    if (card.onClick) {
      card.onClick();
    } else if (card.path) {
      navigate(card.path);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
          <Typography variant="h6">Acesso Rápido</Typography>
        </Box>

        <Grid container spacing={2}>
          {quickAccessCards.map((card) => (
            <Grid item xs={12} sm={4} key={card.title}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: card.highlight ? "#f5fbfa" : "white",
                  border: card.highlight ? "1px solid #00A884" : "none",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(card)}
                  sx={{ height: "100%", p: 1 }}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <card.icon
                      sx={{
                        fontSize: 40,
                        color: card.highlight ? "#00A884" : "primary.main",
                        mb: 1,
                      }}
                    />
                    <Typography variant="subtitle1" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Dialog de seleção de pet */}
      <PetSelectionDialog
        open={openPetDialog}
        onClose={handleClosePetDialog}
        onConfirm={handleConfirmPetSelection}
        onSelectPet={handleSelectPet}
        pets={petsList}
        selectedPetId={selectedPetId}
        loading={loadingPets}
      />

      {/* Modal de adicionar pet */}
      {openAddPetDialog && (
        <PetFormModal
          open={openAddPetDialog}
          onClose={() => setOpenAddPetDialog(false)}
          onSubmit={handleSavePet}
          mode="add"
          formData={petForm}
          onFormChange={handleFormChange}
          loading={loadingAddPet}
          isValid={isFormValid()}
          photoPreviewUrl={petPhotoPreviewUrl}
          onPhotoChange={(base64, previewUrl) => {
            setPetPhotoBase64(base64);
            setPetPhotoPreviewUrl(previewUrl);
          }}
          onPhotoRemove={() => {
            setPetPhotoBase64(null);
            setPetPhotoPreviewUrl(null);
          }}
        />
      )}
    </>
  );
};

export default VetQuickAccess;
